import { type FC, type KeyboardEvent, memo, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
// Import Types
import type { AITemplateChatMessage, DeviceTabType, ProcessTemplateWithAiTemplate, TemplateHistory } from 'types';
// Import Enums
import { DeliveryMethodEnum, DeviceTypeEnum, DisplayModeEnum } from 'enums';
// Import Constants
import { l, DeviceTabInfos, ModeTabInfos, DisplayModeStyles } from 'constants/common';
import { DefaultLanguage } from 'constants/template';
// Import Hooks
import useRequest from 'hooks/UseRequest';
import useCancelableRequests from 'hooks/UseCancelableRequests';
// Import Services
import { TemplateHttpService } from 'services/http';
// Import Components
import { Button, ExtendedInput, Icon, Modal } from '@geneui/components';
import RadioTabGroup from 'components/common/RadioTabGroup';
import HtmlPreview from 'components/common/HtmlPreview';
// Import Styles
import styles from './aiTemplateBuilderModal.module.scss';

interface Props {
    isOpen: boolean;
    onClose: () => void;
    onApply: (template: ProcessTemplateWithAiTemplate) => void;
    deliveryMethod: DeliveryMethodEnum;
    subject: Nullable<string>;
    body: string;
    language: string;
    eventId?: number;
    /** Channels whose content is plain text (SMS) instead of an HTML template. */
    isPlainText?: boolean;
    /** Extracts the previewable html for channels whose body is an envelope (dynamic pop-up). */
    getPreviewHtml?: (body: string) => string;
}

const AITemplateBuilderModal: FC<Props> = ({
    isOpen,
    onClose,
    onApply,
    deliveryMethod,
    subject,
    body,
    language,
    eventId,
    isPlainText = false,
    getPreviewHtml,
}) => {
    const { t } = useTranslation();
    const { doPostRequest } = useRequest();

    const [activeDevice, setActiveDevice] = useState<DeviceTabType>(DeviceTypeEnum.Desktop);
    const [displayMode, setDisplayMode] = useState<DisplayModeEnum>(DisplayModeEnum.LightMode);

    const [messages, setMessages] = useState<AITemplateChatMessage[]>([]);
    const [inputValue, setInputValue] = useState('');
    const [isSending, setIsSending] = useState(false);
    const [suggestions, setSuggestions] = useState<string[]>([]);
    const [areSuggestionsDismissed, setAreSuggestionsDismissed] = useState(false);
    const templateLanguage = language === DefaultLanguage ? null : language;

    const [history, setHistory] = useState<TemplateHistory>({
        versions: [{ Subject: subject, Body: body, Language: templateLanguage }],
        index: 0,
    });

    // Channels whose body is an envelope carry a non-empty wrapper even with no content, so
    // emptiness can only be judged once the previewable part has been extracted.
    const getPreviewBody = (versionBody?: string) => {
        if (!versionBody) return '';
        return getPreviewHtml ? getPreviewHtml(versionBody) : versionBody;
    };

    const currentVersion = history.versions[history.index];
    // While a new version is generating, the active version is what it will be compared against.
    const previousVersion = isSending ? currentVersion : history.versions[history.index - 1];
    // Compare side-by-side only when there's a non-empty previous version; otherwise show a single preview.
    const hasPreviousVersion = Boolean(getPreviewBody(previousVersion?.Body));

    const messageIdRef = useRef(0);
    const messagesEndRef = useRef<HTMLDivElement>(null);
    const previousInteractionIdRef = useRef<Nullable<string>>(null);

    const { processTemplateWithAiRequest } = useCancelableRequests({
        processTemplateWithAiRequest: TemplateHttpService.processTemplateWithAi,
    });

    // Reset the conversation to the current editor template.
    useEffect(() => {
        if (isOpen) {
            setMessages([]);
            setInputValue('');
            setIsSending(false);
            setSuggestions([]);
            setAreSuggestionsDismissed(false);
            previousInteractionIdRef.current = null;
            setHistory({
                versions: [{ Subject: subject, Body: body, Language: templateLanguage }],
                index: 0,
            });
        }
    }, [isOpen, subject, body, templateLanguage]);

    // Keep the latest message in view.
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages, isSending]);

    const handleSend = (overrideMessage?: string) => {
        // `overrideMessage` is set when a request is triggered by clicking a suggestion.
        const userMessage = (overrideMessage ?? inputValue).trim();
        if (!userMessage || isSending) return;

        // Version this prompt branches from. Undo/Redo are disabled while sending,
        // so `branchBase` stays valid for the whole request lifecycle.
        const branchBase = history.index;
        const userMessageId = ++messageIdRef.current;

        setMessages((prev) => [
            ...prev,
            { id: userMessageId, role: 'user', text: userMessage, versionIndex: branchBase },
        ]);
        // Keep the input intact for suggestion-triggered sends; clear it for typed messages.
        if (overrideMessage === undefined) setInputValue('');
        setIsSending(true);

        doPostRequest(processTemplateWithAiRequest.request, {
            requestBody: {
                DeliveryMethod: deliveryMethod,
                Template: currentVersion,
                UserMessage: userMessage,
                PreviousInteractionId: previousInteractionIdRef.current,
                ...(eventId ? { EventId: eventId } : {}),
            },
            successCallback: (data) => {
                const response = data?.Response;
                if (!response) return;

                previousInteractionIdRef.current = data.InteractionId ?? previousInteractionIdRef.current;

                setSuggestions(response.suggestions ?? []);

                const text = response.responseMessage || response.error;
                const isSuccess = !response.error && Boolean(response.body);

                if (isSuccess) {
                    const newIndex = branchBase + 1;

                    setHistory((prev) => {
                        const active = prev.versions[branchBase];
                        const next: ProcessTemplateWithAiTemplate = {
                            ...active,
                            Subject: response.subject ?? active.Subject,
                            Body: response.body ?? active.Body,
                        };
                        const kept = prev.versions.slice(0, branchBase + 1);
                        return { versions: [...kept, next], index: newIndex };
                    });

                    setMessages((prev) => {
                        // A successful prompt from an undone position discards the "future":
                        // drop the disabled messages we branched away from, but keep the prompt
                        // that started this turn.
                        const pruned = prev.filter((m) => m.versionIndex <= branchBase || m.id === userMessageId);
                        // Anchor that prompt to the version it produced so it greys out together
                        // with its reply the next time this step is undone.
                        const reanchored = pruned.map((m) =>
                            m.id === userMessageId ? { ...m, versionIndex: newIndex } : m,
                        );
                        return text
                            ? [
                                  ...reanchored,
                                  { id: ++messageIdRef.current, role: 'assistant', text, versionIndex: newIndex },
                              ]
                            : reanchored;
                    });

                    return;
                }

                // No new version was produced (error / empty body): keep the reply anchored to
                // the current version so nothing is greyed or discarded.
                if (text) {
                    setMessages((prev) => [
                        ...prev,
                        { id: ++messageIdRef.current, role: 'assistant', text, versionIndex: branchBase },
                    ]);
                }
            },
        }).finally(() => setIsSending(false));
    };

    const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    };

    const handleSuggestionClick = (suggestion: string) => handleSend(suggestion);

    const handleDismissSuggestions = () => setAreSuggestionsDismissed(true);

    const showSuggestions = !areSuggestionsDismissed && suggestions.length > 0;

    const canApply = !isSending && history.index > 0;
    const canUndo = !isSending && history.index > 0;
    const canRedo = !isSending && history.index < history.versions.length - 1;

    const handleApply = () => {
        if (!canApply) return;
        onApply(currentVersion);
        onClose();
    };

    // Step back one version; messages ahead of the new index grey out but stay in the log.
    const handleUndo = () => {
        if (!canUndo) return;
        setHistory((prev) => ({ ...prev, index: prev.index - 1 }));
    };

    // Step forward one version; the greyed-out messages of that step return to normal.
    const handleRedo = () => {
        if (!canRedo) return;
        setHistory((prev) => ({ ...prev, index: prev.index + 1 }));
    };

    const isDarkMode = displayMode === DisplayModeEnum.DarkMode;

    const previewBodyStyle = isDarkMode
        ? `${DisplayModeStyles[displayMode]} html,body{background:#171c26}`
        : `${DisplayModeStyles[displayMode]} html,body{background:#fff}`;

    const deviceClassName = classNames({
        [styles.deviceMobile]: activeDevice === DeviceTypeEnum.Mobile,
        [styles.deviceTablet]: activeDevice === DeviceTypeEnum.Tablet,
        [styles.deviceDesktop]: activeDevice === DeviceTypeEnum.Desktop,
    });

    const renderPreview = (template?: ProcessTemplateWithAiTemplate, isLoading = false) => {
        if (isLoading) {
            return (
                <div className={styles.skeletonCard}>
                    <span className={classNames(styles.skeletonBar, styles.skeletonHeader)} />
                    <span className={styles.skeletonBlock} />
                    <span className={classNames(styles.skeletonBar, styles.skeletonLineWide)} />
                    <span className={classNames(styles.skeletonBar, styles.skeletonLineNarrow)} />
                </div>
            );
        }

        const previewBody = getPreviewBody(template?.Body);

        if (previewBody && isPlainText) {
            return (
                <div className={styles.previewPlainText}>
                    <div className={styles.previewPlainTextBody}>{previewBody}</div>
                </div>
            );
        }

        if (previewBody) {
            return (
                <div className={classNames(styles.previewDevice, deviceClassName)}>
                    {template?.Subject && (
                        // NOTE: the light/dark switch previews the template body only, so the
                        // subject section keeps following the app theme.
                        <div className={styles.previewSubject}>
                            <span className={styles.previewSubjectText}>
                                {t(l.Subject)}: {template?.Subject}
                            </span>
                        </div>
                    )}
                    <div
                        className={classNames(styles.previewFrame, {
                            [styles.previewFrameDark]: isDarkMode,
                        })}
                    >
                        <HtmlPreview
                            className={styles.previewIframe}
                            value={previewBody}
                            styleContent={previewBodyStyle}
                            autoHeight
                            minHeight={260}
                        />
                    </div>
                </div>
            );
        }

        return (
            <div className={styles.previewCard}>
                <div className={styles.previewIcon}>
                    <Icon type="bc-icon-AI" />
                </div>
                <h3 className={styles.previewTitle}>{t(l.YourAIGeneratedTemplateWillAppearHere)}</h3>
                <p className={styles.previewDescription}>{t(l.PickAFewSuggestionsAddAnyDetailsThenHitGenerate)}</p>
            </div>
        );
    };

    const modalTitle = (
        <div className={styles.headerBar}>
            <div className={styles.header}>
                <div className={styles.headerIcon}>
                    <Icon type="bc-icon-AI" />
                </div>
                <div className={styles.headerText}>
                    <span className={styles.headerTitle}>{t(l.AITemplateBuilder)}</span>
                    <span className={styles.headerSubtitle}>
                        <span className={styles.headerStatusDot} />
                        {t(l.PoweredByGemini)}
                    </span>
                </div>
            </div>
            {!isPlainText && (
                <div className={styles.toolbar}>
                    <RadioTabGroup<DeviceTabType>
                        activeTab={activeDevice}
                        setActiveTab={setActiveDevice}
                        tabs={DeviceTabInfos}
                    />
                    <RadioTabGroup<DisplayModeEnum>
                        activeTab={displayMode}
                        setActiveTab={setDisplayMode}
                        tabs={ModeTabInfos}
                    />
                </div>
            )}
        </div>
    );

    const modalFooter = (
        <div className={styles.footer}>
            <div className={styles.footerActions}>
                <Button
                    appearance="minimal"
                    color="default"
                    icon="bc-icon-undo1"
                    onClick={handleUndo}
                    disabled={!canUndo}
                >
                    {t(l.Undo)}
                </Button>
                <Button
                    appearance="minimal"
                    color="default"
                    icon="bc-icon-redo1"
                    onClick={handleRedo}
                    disabled={!canRedo}
                >
                    {t(l.Redo)}
                </Button>
            </div>
            <Button icon="bc-icon-check" onClick={handleApply} disabled={!canApply}>
                {t(l.ApplyChanges)}
            </Button>
        </div>
    );

    return (
        <Modal
            background="dark-background"
            position="center"
            size="content-size"
            className={styles.modal}
            title={modalTitle}
            footer={modalFooter}
            closable={true}
            closeOnClickOutside={false}
            visible={isOpen}
            onCancel={onClose}
        >
            <div className={styles.content}>
                <section className={classNames(styles.preview, { [styles.previewDark]: isDarkMode })}>
                    {hasPreviousVersion ? (
                        <>
                            <div className={styles.previewColumn}>
                                <span className={classNames(styles.previewBadge, styles.previewBadgePrevious)}>
                                    {t(l.Previous)}
                                </span>
                                <div className={styles.previewStage}>{renderPreview(previousVersion)}</div>
                            </div>
                            <div className={styles.previewColumn}>
                                <span className={classNames(styles.previewBadge, styles.previewBadgeCurrent)}>
                                    {t(l.Current)}
                                </span>
                                <div className={styles.previewStage}>{renderPreview(currentVersion, isSending)}</div>
                            </div>
                        </>
                    ) : (
                        <div className={styles.previewStage}>{renderPreview(currentVersion, isSending)}</div>
                    )}
                </section>

                <aside className={styles.sidebar}>
                    <div className={styles.messages}>
                        {messages.map(({ id, role, text, versionIndex }) => (
                            <div
                                key={id}
                                className={classNames(styles.message, {
                                    [styles.messageUser]: role === 'user',
                                    [styles.messageAssistant]: role === 'assistant',
                                    [styles.messageDisabled]: versionIndex > history.index,
                                })}
                            >
                                {text}
                            </div>
                        ))}
                        {isSending && (
                            <div className={classNames(styles.message, styles.messageAssistant, styles.typing)}>
                                <span className={styles.typingDot} />
                                <span className={styles.typingDot} />
                                <span className={styles.typingDot} />
                            </div>
                        )}
                        <div ref={messagesEndRef} />
                    </div>
                    {showSuggestions && (
                        <div className={styles.suggestions}>
                            <div className={styles.suggestionsHeader}>
                                <div className={styles.suggestionsIcon}>
                                    <Icon type="bc-icon-AI" />
                                </div>
                                <div className={styles.suggestionsHeaderText}>
                                    <span className={styles.suggestionsTitle}>{t(l.AISuggestions)}</span>
                                    <span className={styles.suggestionsSubtitle}>
                                        {t(l.SuggestedListOfImprovements)}
                                    </span>
                                </div>
                                <Button
                                    className={styles.suggestionsClose}
                                    appearance="minimal"
                                    color="default"
                                    icon="bc-icon-clear"
                                    onClick={handleDismissSuggestions}
                                />
                            </div>
                            {isSending ? (
                                <div className={styles.suggestionsList}>
                                    <span
                                        className={classNames(styles.suggestionSkeleton, styles.suggestionSkeletonWide)}
                                    />
                                    <span
                                        className={classNames(
                                            styles.suggestionSkeleton,
                                            styles.suggestionSkeletonNarrow,
                                        )}
                                    />
                                    <span
                                        className={classNames(
                                            styles.suggestionSkeleton,
                                            styles.suggestionSkeletonMedium,
                                        )}
                                    />
                                </div>
                            ) : (
                                <div className={styles.suggestionsList}>
                                    {suggestions.map((suggestion, index) => (
                                        <button
                                            key={index}
                                            type="button"
                                            className={styles.suggestionPill}
                                            onClick={() => handleSuggestionClick(suggestion)}
                                        >
                                            {suggestion}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                    <div className={styles.inputBox}>
                        <ExtendedInput
                            type="textarea"
                            className={styles.inputField}
                            placeholder={t(l.DescribeYourTemplateIdea)}
                            rows={3}
                            value={inputValue}
                            onChange={(e) => setInputValue(e.target.value)}
                            onKeyDown={handleKeyDown}
                            disabled={isSending}
                        />
                        <div className={styles.inputActions}>
                            <div className={styles.inputTools}>
                                {/* TODO: temporarily hidden — no need for now */}
                                {/* <Button appearance="minimal" color="default" icon="bc-icon-edit-pen" />
                                <Button appearance="minimal" color="default" icon="bc-icon-publish-globe-48" />
                                <Button appearance="minimal" color="default" icon="bc-icon-add" /> */}
                            </div>
                            <Button
                                className={styles.sendButton}
                                color="default"
                                icon="bc-icon-sent-outline"
                                onClick={() => handleSend()}
                                disabled={isSending || !inputValue.trim()}
                            />
                        </div>
                    </div>
                </aside>
            </div>
        </Modal>
    );
};

export default memo(AITemplateBuilderModal);
